import { getContext, getLinkToFmBucketFile, sliceText } from '@/plugins/helper'
import { ensureUTCForDateWithoutTimezone } from '@fmpedia/helpers'

export const PAGE_SCHEME_TYPE = {
  NEWS_ARTICLE: 'NewsArticle',
  ARTICLE: 'Article',
  WEB_SITE: 'WebSite',
  ORGANIZATION: 'Organization',
  PRODUCT: 'Product',
  SERVICE: 'Service',
  PROFESSIONAL_SERVICE: 'ProfessionalService',
  WEB_PAGE: 'WebPage',
  CONTACT_PAGE: 'ContactPage',
  EVENT: 'Event',
  PERSON: 'Person',
  VIDEO_GENERAL: 'VideoObjectGeneral'
}

const VIDEO_DESCRIPTION_LIMIT = 300
const SCHEME_TEXT_LIMIT = 1000

const LOGO_WIDTH = 263
const LOGO_HEIGHT = 110

/**
 * Company facts for the Organization schema.
 * legalName, address, and registration number are the official Cyprus entity (FMA-2410).
 */
const ORGANIZATION_FACTS = {
  name: 'Finance Magnates',
  legalName: 'FINANCE MAGNATES CY LIMITED',
  description:
    'Finance Magnates - the world’s only multi-asset online trading knowledge hub - offers financial news, industry research, international events and more',
  founder: {
    '@type': 'Person',
    name: 'Michael Greenberg',
    jobTitle: 'Founder'
  },
  ceo: {
    '@type': 'Person',
    name: 'Neophytos Papageorgiou',
    jobTitle: 'CEO'
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Apostolou Andrea 11, Hyper Tower, 3rd Floor',
    addressLocality: 'Mesa Geitonia',
    addressRegion: 'Limassol',
    postalCode: '4007',
    addressCountry: 'CY'
  },
  identifier: {
    '@type': 'PropertyValue',
    name: 'Registration Number',
    value: 'HE 433362'
  }
}

const ORGANIZATION_SAME_AS_ENV_KEYS = [
  'SOCIAL_LINK_LINKEDIN_FM',
  'SOCIAL_LINK_TWITTER_FM',
  'SOCIAL_LINK_FACEBOOK_FM',
  'SOCIAL_LINK_YOUTUBE_FM',
  'SOCIAL_LINK_TELEGRAM_FM'
]

function getDomain() {
  return `${getContext().store.$env.DOMAIN_URL}/`
}

function getLogoPath() {
  return getLinkToFmBucketFile('fm-logo-black.png')
}

function getCurrentYear() {
  return new Date().getFullYear()
}

function formatVideoDescription(description) {
  return sliceText({ text: description, limit: VIDEO_DESCRIPTION_LIMIT }).trim()
}

function formatText(text) {
  return sliceText({ text, limit: SCHEME_TEXT_LIMIT })
}

function numericOrUndefined(value) {
  if (value === '' || value == null) return undefined

  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

function getOrganizationSameAs() {
  const env = getContext().store.$env || {}

  return ORGANIZATION_SAME_AS_ENV_KEYS.map(key => env[key]).filter(Boolean)
}

function getOrganizationLogo() {
  return {
    '@type': 'ImageObject',
    url: getLogoPath(),
    width: LOGO_WIDTH,
    height: LOGO_HEIGHT
  }
}

export const PAGE_SCHEME = {
  [PAGE_SCHEME_TYPE.WEB_SITE]: ({
    genre = 'financial news website',
    headline = 'Financial and Business News | Finance Magnates',
    isBasedOnUrl = getDomain(),
    text = ORGANIZATION_FACTS.description,
    thumbnailUrl = getLogoPath(),
    description = ORGANIZATION_FACTS.description
  } = {}) => ({
    '@type': 'WebSite',
    genre,
    headline,
    isBasedOnUrl,
    keywords: ['Finance news', 'finance', 'finance magnates'],
    text: formatText(text),
    thumbnailUrl,
    description
  }),
  [PAGE_SCHEME_TYPE.NEWS_ARTICLE]: ({
    dateline = '',
    articleBody = '',
    articleSection = '',
    wordCount = '',
    alternativeHeadline = '',
    copyrightYear = getCurrentYear(),
    datePublished = '',
    dateModified = '',
    genre = '',
    headline = '',
    isFamilyFriendly = true,
    text = '',
    thumbnailUrl = '',
    description = '',
    author,
    mainEntityOfPage = ''
  } = {}) => ({
    '@type': 'NewsArticle',
    dateline,
    articleBody,
    articleSection,
    wordCount: numericOrUndefined(wordCount),
    alternativeHeadline,
    copyrightYear: numericOrUndefined(copyrightYear),
    datePublished,
    dateModified,
    genre,
    headline,
    isFamilyFriendly,
    text: formatText(text),
    thumbnailUrl,
    description,
    ...(author ? { author } : {}),
    image: thumbnailUrl,
    publisher: {
      '@type': 'Organization',
      name: ORGANIZATION_FACTS.name,
      logo: getOrganizationLogo()
    },
    mainEntityOfPage
  }),
  [PAGE_SCHEME_TYPE.ARTICLE]: ({
    articleBody = '',
    articleSection = '',
    wordCount = '',
    alternativeHeadline = '',
    copyrightYear = getCurrentYear(),
    datePublished = '',
    dateModified = '',
    discussionUrl = '',
    headline = '',
    keywords = '',
    thumbnailUrl = '',
    timeRequired = '',
    alternateName = '',
    description = '',
    author,
    mainEntityOfPage = ''
  } = {}) => ({
    '@type': 'Article',
    articleBody,
    articleSection,
    wordCount: numericOrUndefined(wordCount),
    alternativeHeadline,
    copyrightYear: numericOrUndefined(copyrightYear),
    datePublished,
    dateModified,
    discussionUrl,
    headline,
    keywords,
    thumbnailUrl,
    timeRequired,
    alternateName,
    description,
    ...(author ? { author } : {}),
    image: thumbnailUrl,
    publisher: {
      '@type': 'Organization',
      name: ORGANIZATION_FACTS.name,
      logo: getOrganizationLogo()
    },
    mainEntityOfPage
  }),
  /** DO NOT REMOVE - leave until implemented pages schemes approved by client **/
  // [PAGE_SCHEME_TYPE.NEWS_ARTICLE]: ({
  //   id,
  //   headline,
  //   imageUrl,
  //   datePublished,
  //   dateModified,
  //   authorName,
  //   description
  // }) =>
  //   `"@type": "WebPage",
  //    "@id": "${id}",
  //    "mainEntityOfPage": {
  //        "@type": "NewsArticle",
  //        "@id": "${id}"
  //     },
  //    "headline": "${headline}",
  //    "image": {
  //        "@type": "ImageObject",
  //        "url": "${imageUrl}",
  //        "height": 100,
  //        "width": 180
  //     },
  //    "datePublished": "${datePublished}",
  //    "dateModified": "${dateModified}",
  //    "author": {
  //        "@type": "Person",
  //        "name": "${authorName}"
  //     },
  //    "publisher": {
  //        "@type": "Organization",
  //        "name": "Finance Magnates",
  //        "logo": {
  //            "@type": "ImageObject",
  //            "url": "https://www.financemagnates.com/wp-content/uploads/sites/8/2016/02/financemagnates.png",
  //            "width": 263,
  //            "height": 110
  //        }
  //     },
  //    "description": "${description}"
  //   }`,
  [PAGE_SCHEME_TYPE.ORGANIZATION]: () => {
    const sameAs = getOrganizationSameAs()

    return {
      '@type': 'Organization',
      name: ORGANIZATION_FACTS.name,
      legalName: ORGANIZATION_FACTS.legalName,
      url: getDomain(),
      logo: getOrganizationLogo(),
      description: ORGANIZATION_FACTS.description,
      founder: ORGANIZATION_FACTS.founder,
      employee: ORGANIZATION_FACTS.ceo,
      address: ORGANIZATION_FACTS.address,
      identifier: ORGANIZATION_FACTS.identifier,
      ...(sameAs.length ? { sameAs } : {})
    }
  },
  [PAGE_SCHEME_TYPE.PRODUCT]: ({
    productID = 'Intelligence',
    alternateName = 'Intelligence Products',
    description = 'Our Quarterly Report incorporates unique data on trading volumes and traders, in-depth insights, special guest articles, regional information, regulations, updates and much more.',
    mainEntityOfPage = 'intelligence',
    brand = 'Finance Magnates',
    image = '',
    name = 'intelligence'
  } = {}) => ({
    '@type': 'Product',
    productID,
    alternateName,
    description,
    mainEntityOfPage,
    brand,
    image,
    name
  }),
  [PAGE_SCHEME_TYPE.SERVICE]: ({
    serviceType = 'Intelligence',
    alternateName = 'Quarterly Industry Report',
    description = 'Our Quarterly Report incorporates unique data on trading volumes and traders, in-depth insights, special guest articles, regional information, regulations, updates and much more.',
    mainEntityOfPage = 'Main intelligence page',
    name = 'Finance Magnates Intelligence Products'
  } = {}) => ({
    '@type': 'Service',
    serviceType,
    alternateName,
    description,
    mainEntityOfPage,
    name
  }),
  [PAGE_SCHEME_TYPE.PROFESSIONAL_SERVICE]: ({
    legalName = 'Finance Magnates',
    logoUrl = '',
    alternateName = 'QIR',
    description = 'QIR',
    imageUrl = '',
    mainEntityOfPage = 'Finance Magnates Products',
    name = 'finance Magnates Quarterly reports',
    url = ''
  } = {}) => ({
    '@type': 'ProfessionalService',
    legalName,
    ...(logoUrl
      ? {
          logo: {
            '@type': 'ImageObject',
            contentUrl: logoUrl,
            embedUrl: logoUrl,
            discussionUrl: logoUrl,
            isBasedOnUrl: logoUrl,
            thumbnailUrl: logoUrl
          }
        }
      : {}),
    alternateName,
    description,
    ...(imageUrl
      ? {
          image: {
            '@type': 'ImageObject',
            contentUrl: imageUrl,
            embedUrl: imageUrl,
            discussionUrl: imageUrl,
            url: imageUrl
          }
        }
      : {}),
    mainEntityOfPage,
    name,
    url
  }),
  [PAGE_SCHEME_TYPE.WEB_PAGE]: ({
    relatedLink = '',
    significantLink = '',
    alternativeHeadline = '',
    copyrightYear = getCurrentYear(),
    headline = '',
    isAccessibleForFree = true,
    keywords = '',
    text = '',
    thumbnailUrl = '',
    description = '',
    mainEntityOfPage = '',
    name = ''
  } = {}) => ({
    '@type': 'WebPage',
    relatedLink,
    significantLink,
    alternativeHeadline,
    copyrightYear: numericOrUndefined(copyrightYear),
    headline,
    isAccessibleForFree,
    keywords,
    text: formatText(text),
    thumbnailUrl,
    description,
    mainEntityOfPage,
    name
  }),
  [PAGE_SCHEME_TYPE.CONTACT_PAGE]: ({
    relatedLink = '',
    significantLink = '',
    headline = 'Contact Us',
    isBasedOnUrl = '',
    keywords = 'Contact Us',
    text = 'Thank you for using Finance Magnates. Please fill out the quick form and we will be in touch with lightning speed',
    description = 'contact us page',
    name = '/contact-us'
  } = {}) => ({
    '@type': 'ContactPage',
    relatedLink,
    significantLink,
    headline,
    isBasedOnUrl,
    keywords,
    text: formatText(text),
    description,
    name
  }),
  [PAGE_SCHEME_TYPE.EVENT]: ({
    endDate = '',
    isAccessibleForFree = false,
    previousStartDate = '',
    startDate = '',
    alternateName = '',
    description = '',
    name = ''
  } = {}) => ({
    '@type': 'Event',
    endDate,
    isAccessibleForFree,
    previousStartDate,
    startDate,
    alternateName,
    description,
    name
  }),
  [PAGE_SCHEME_TYPE.PERSON]: ({
    name = '',
    jobTitle = '',
    url = '',
    description = '',
    image = '',
    sameAs = [],
    worksFor
  } = {}) => ({
    '@type': 'Person',
    name,
    jobTitle,
    url,
    description,
    image,
    ...(sameAs.length ? { sameAs } : {}),
    ...(worksFor ? { worksFor } : {})
  }),
  [PAGE_SCHEME_TYPE.VIDEO_GENERAL]: ({
    name = '',
    description = '',
    thumbnailUrl = '',
    uploadDate = '',
    embedUrl = ''
  } = {}) => ({
    '@type': 'VideoObject',
    name,
    description: formatVideoDescription(description) || name,
    thumbnailUrl,
    uploadDate: ensureUTCForDateWithoutTimezone(uploadDate),
    embedUrl
  })
}
